import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewEncapsulation,
  booleanAttribute,
  computed,
  inject,
  input,
  numberAttribute,
  output,
  signal
} from '@angular/core';
import { OSKColorVariant, OSKIconName, OSKSize } from '../../types/osk-theme.types';
import { OSKIconComponent } from '../../atoms/osk-icon/osk-icon.component';

/**
 * Molecule: <osk-card>
 * Surface container for dashboard blocks, settings panels, selection cards, and details cards.
 * Supports optional header (icon + title + subtitle + [oskCardActions] slot), [oskCardFooter] slot,
 * and interactive selection states ([clickable], [selected], (cardClick)).
 */
@Component({
  selector: 'osk-card',
  standalone: true,
  imports: [OSKIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrl: './osk-card.component.scss',
  template: `
    <section
      [class]="cardClasses()"
      [attr.tabindex]="clickable() ? 0 : null"
      [attr.role]="ariaRole()"
      [attr.aria-pressed]="ariaRole() === 'button' && selected() ? true : null"
      [attr.aria-checked]="(ariaRole() === 'checkbox' || ariaRole() === 'radio') && selected() ? true : null"
      (click)="onCardClick($event)"
      (keydown.enter)="onCardKeydown($event)"
      (keydown.space)="onCardKeydown($event)"
    >
      @if (coverImage()) {
        <div class="osk-card__cover" [style.height.px]="coverHeight()">
          <img
            class="osk-card__cover-img"
            [src]="coverImage()!"
            [alt]="coverAlt() || title() || ''"
          />
        </div>
      }

      @if (title() || subtitle() || icon()) {
        <header [class.osk-card__header]="true" [class.osk-card__header--borderless]="!headerBorder()">
          <div class="osk-card__heading">
            @if (icon()) {
              <span [class]="'osk-card__icon osk-card__icon--' + color()">
                <osk-icon [name]="icon()!" [size]="18"></osk-icon>
              </span>
            }
            <div class="osk-card__titles">
              @if (title()) {
                <h3 class="osk-card__title">{{ title() }}</h3>
              }
              @if (subtitle()) {
                <p class="osk-card__subtitle">{{ subtitle() }}</p>
              }
            </div>
          </div>
          <div class="osk-card__actions"><ng-content select="[oskCardActions]"></ng-content></div>
        </header>
      }

      <div class="osk-card__body"><ng-content></ng-content></div>

      <footer class="osk-card__footer"><ng-content select="[oskCardFooter]"></ng-content></footer>
    </section>
  `
})
export class OSKCardComponent implements AfterViewInit {
  private readonly el = inject(ElementRef);
  private readonly hasActions = signal(false);

  readonly role = input<string | null | undefined>(undefined);
  readonly title = input<string | undefined>(undefined);
  readonly subtitle = input<string | undefined>(undefined);
  readonly icon = input<OSKIconName | undefined>(undefined);
  readonly color = input<OSKColorVariant>('primary');
  readonly padding = input<OSKSize | 'none'>('md');
  readonly hoverable = input(false, { transform: booleanAttribute });
  readonly clickable = input(false, { transform: booleanAttribute });
  readonly selected = input(false, { transform: booleanAttribute });
  readonly headerBorder = input(true, { transform: booleanAttribute });
  readonly coverImage = input<string | undefined>(undefined);
  readonly coverAlt = input<string>('');
  readonly coverHeight = input(92, { transform: numberAttribute });

  readonly cardClick = output<Event>();

  protected readonly ariaRole = computed<string | null>(() => {
    const explicitRole = this.role();
    if (explicitRole !== undefined) {
      return explicitRole;
    }
    // Per W3C ARIA specifications, a role="button" must not contain nested interactive controls.
    // If the card projects action controls, omit the outer button role.
    if (this.hasActions()) {
      return null;
    }
    return this.clickable() ? 'button' : null;
  });

  ngAfterViewInit(): void {
    const actionsEl = (this.el.nativeElement as HTMLElement).querySelector('.osk-card__actions');
    if (actionsEl && actionsEl.children.length > 0) {
      queueMicrotask(() => this.hasActions.set(true));
    }
  }

  protected readonly cardClasses = computed<string>(() => {
    const classes: string[] = ['osk-card', `osk-card--pad-${this.padding()}`];
    if (this.hoverable() || this.clickable()) {
      classes.push('osk-card--hoverable');
    }
    if (this.clickable()) {
      classes.push('osk-card--clickable');
    }
    if (this.selected()) {
      classes.push('osk-card--selected');
    }
    if (this.coverImage()) {
      classes.push('osk-card--has-cover');
    }
    return classes.join(' ');
  });

  protected onCardClick(event: MouseEvent): void {
    if (!this.clickable()) {
      return;
    }
    const target = event.target as HTMLElement | null;
    const currentTarget = event.currentTarget as HTMLElement | null;
    const interactiveEl = target?.closest(
      'button, a, input, select, textarea, label, [role="button"], [role="menuitem"], [role="checkbox"], [role="switch"], [role="combobox"], [role="option"], [role="tab"], osk-button, osk-action-menu, osk-checkbox, osk-switch, osk-select, osk-date-picker'
    );
    if (interactiveEl && interactiveEl !== currentTarget) {
      return;
    }
    this.cardClick.emit(event);
  }

  protected onCardKeydown(event: Event): void {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (this.clickable()) {
      event.preventDefault();
      this.cardClick.emit(event);
    }
  }
}
